import React from 'react';
import { connect } from 'react-redux';
import { getParentOffset, getParents } from './utils';
import { getByID, isNullOrEmpty } from '../../utils/core';
import { path } from 'ramda';
import { getElementStyles, COLOUR_MAPPINGS } from './elements/elementStyles';

/**
 * Start / end positions calculated like this
 * 
 * Start on the offset that minimizes the distance between the map elements
 * 
 * End on opposite edge to start edge
 */

export const OFFSET_SIDE = {
    TOP: 'TOP',
    BOTTOM: 'BOTTOM',
    LEFT: 'LEFT',
    RIGHT: 'RIGHT',
    NONE: 'NONE',
};

/** returns the opposite side to the given side */
const reverseSide = (side) => {
    switch (side) {
    case OFFSET_SIDE.TOP: return OFFSET_SIDE.BOTTOM;
    case OFFSET_SIDE.BOTTOM: return OFFSET_SIDE.TOP;
    case OFFSET_SIDE.LEFT: return OFFSET_SIDE.RIGHT;
    case OFFSET_SIDE.RIGHT: return OFFSET_SIDE.LEFT;
    case OFFSET_SIDE.NONE: default: return OFFSET_SIDE.NONE;
    }
};

// Rotation is based off the arrowhead polygon that points up /\
// And so rotation 0 means it goes up into the bottom offset by default
/** returns the outcome offset on the given side for an element of the given width and height */
export const getOffset = (width, height, side) => {
    switch (side) {
    case OFFSET_SIDE.TOP:
        return { x: width / 2, y: 0, rotation: 180 };
    case OFFSET_SIDE.BOTTOM:
        return { x: width / 2, y: height, rotation: 0 };
    case OFFSET_SIDE.LEFT:
        return { x: 0, y: height / 2, rotation: 90 };
    case OFFSET_SIDE.RIGHT:
        return { x: width, y: height / 2, rotation: -90 };
    case OFFSET_SIDE.NONE:
    default:
        return { x: 0, y: 0, rotation: 0 };
    }
};

export const Outcome = (props) => {
    const {
        outcome,
        fromParentOffsetX,
        fromParentOffsetY,
        nextParentOffsetX,
        nextParentOffsetY,
        highestClosedFromParent,
        highestClosedNextParent,
        doNotRender,
    } = props;

    let {
        mapElement,
        nextMapElement,
    } = props;
    
    if (doNotRender) return (null);

    const highestClosedFromParentId = path(['id'], highestClosedFromParent);
    const highestClosedNextParentId = path(['id'], highestClosedNextParent);
    // if the start and end of this outcome are in the same closed group, then don't render this
    if (highestClosedFromParentId !== undefined && highestClosedFromParentId === highestClosedNextParentId) return (null);

    // set start and end element to closed group element if it exists
    mapElement = highestClosedFromParent || mapElement;
    // falls back to pointer location when dragging
    nextMapElement = highestClosedNextParent || nextMapElement || outcome.next;
    // Some outcomes are allowed to be saved without destinations
    // for these we can not render anything and so render nothing
    if (!nextMapElement) {
        return null;
    }

    const fromWidth = mapElement.width || getElementStyles(mapElement.elementType).mapElement.width;
    const fromHeight = mapElement.height || getElementStyles(mapElement.elementType).mapElement.height;

    let nextWidth = 0;
    let nextHeight = 0;
    if (nextMapElement.elementType) {
        // take group element size or fall back to styles for map elements
        nextWidth = nextMapElement.width || getElementStyles(nextMapElement.elementType).mapElement.width;
        nextHeight = nextMapElement.height || getElementStyles(nextMapElement.elementType).mapElement.height;
    }

    const fromParentOffset = {
        x: fromParentOffsetX,
        y: fromParentOffsetY,
    };
    const nextParentOffset = {
        x: nextParentOffsetX,
        y: nextParentOffsetY,
    };

    const bestOffsets = [OFFSET_SIDE.TOP, OFFSET_SIDE.BOTTOM, OFFSET_SIDE.LEFT, OFFSET_SIDE.RIGHT].reduce(
        (currentBestOffsets, side) => {
            const startOffset = getOffset(fromWidth, fromHeight, side);
            const endOffset = getOffset(nextWidth, nextHeight, reverseSide(side));
            const difference = {
                x: (nextMapElement.x + endOffset.x + nextParentOffset.x) - (mapElement.x + startOffset.x + fromParentOffset.x),
                y: (nextMapElement.y + endOffset.y + nextParentOffset.y) - (mapElement.y + startOffset.y + fromParentOffset.y),
            };
            // Pythagoras's theorem
            // |\
            // b c
            // |_a_\
            // a^2 + b^2 = c^2
            // where a and b form a right angle and c is the hypotenuse of the triangle
            // c = sqrt(a^2 + b^2)
            const distanceToOffset = Math.sqrt((difference.x ** 2) + (difference.y ** 2));
            if (
                currentBestOffsets.distanceToOffset === null ||
                currentBestOffsets.distanceToOffset > distanceToOffset
            ) {
                return ({ distanceToOffset, startOffset, endOffset });
            }
            return currentBestOffsets;
        },
        { distanceToOffset: null, startOffset: getOffset(0, 0, OFFSET_SIDE.NONE), endOffset: getOffset(0, 0, OFFSET_SIDE.NONE) },
    );

    const { startOffset, endOffset } = bestOffsets;

    const points = [
        {
            x: mapElement.x + startOffset.x + fromParentOffset.x,
            y: mapElement.y + startOffset.y + fromParentOffset.y,
        },
    ];

    points.push({
        x: nextMapElement.x + endOffset.x + nextParentOffset.x, 
        y: nextMapElement.y + endOffset.y + nextParentOffset.y, 
    });

    const midpoint = {
        x: points[0].x + (points[1].x - points[0].x) / 2,
        y: points[0].y + (points[1].y - points[0].y) / 2,
    };

    let d = `M ${points[0].x} ${points[0].y}`;
    
    if (
        startOffset.rotation === getOffset(0, 0, OFFSET_SIDE.LEFT).rotation || 
        startOffset.rotation === getOffset(0, 0, OFFSET_SIDE.RIGHT).rotation
    ) {
        d += `L ${midpoint.x} ${points[0].y}`;
        d += `L ${midpoint.x} ${points[1].y}`;
    } else {
        d += `L ${points[0].x} ${midpoint.y}`;
        d += `L ${points[1].x} ${midpoint.y}`;
    }

    // Stop a little short of the map element so the border doesn't cover the arrow head point
    switch (endOffset.rotation) {
    case getOffset(0, 0, OFFSET_SIDE.TOP).rotation:
        d += `L ${points[1].x} ${points[1].y - 1}`;
        break;
    case getOffset(0, 0, OFFSET_SIDE.BOTTOM).rotation:
        d += `L ${points[1].x} ${points[1].y + 1}`;
        break;
    case getOffset(0, 0, OFFSET_SIDE.LEFT).rotation:
        d += `L ${points[1].x - 1} ${points[1].y}`;
        break;
    case getOffset(0, 0, OFFSET_SIDE.RIGHT).rotation:
        d += `L ${points[1].x + 1} ${points[1].y}`;
        break;
    default:
        d += `L ${points[1].x} ${points[1].y}`;
    }

    return <>
        <path d={d} strokeWidth="1" stroke={COLOUR_MAPPINGS.outcome} fill="none" />
        <polygon
            fill={COLOUR_MAPPINGS.outcome}
            transform={`
                translate(${points[1].x} ${points[1].y})
                rotate(${endOffset.rotation})
            `}
            // Together, (0,0), (3,6), (-3,6), back to (0,0)
            // draw an arrow pointing up
            //        (0,0)
            //        /  \
            //       /    \
            //      /      \
            // (-3,6)------(3,6)
            points="0,0 3,6 -3,6"
        />
    </>;
};

export const mapStateToProps = (state, ownProps) => {
    // When the user is dragging to make a new outcome we add a 'next' property on the outcome
    // so if this exists then we are one of these dragging outcomes
    // If we don't have this then we are a saved outcome from the api
    const isDraggingOutcome = !isNullOrEmpty(ownProps.outcome.next);
    const nextMapElement = isDraggingOutcome ?
        // These fake outcomes will use the element the user is hovering over (if any)
        getByID(state.graphEditor.hoveringMapElementId, state.mapElements) :
        getByID(ownProps.outcome.nextMapElementId, state.mapElements);

    // Find parentOffsets and open status
    const fromMapElement = ownProps.mapElement;

    if (
        nextMapElement && nextMapElement.groupElementId && state.groupElements.find(group => group.id === nextMapElement.groupElementId) === undefined ||
        fromMapElement.groupElementId && state.groupElements.find(group => group.id === fromMapElement.groupElementId) === undefined
    ) {
        // Do not render if the nextMapElement or fromMapElement does not exist
        return { doNotRender: true };
    }

    let fromParents = getParents(fromMapElement, state.groupElements);
    // find the highest fromParent that is closed
    const highestClosedFromParent = fromParents.reverse().find(parent => !parent.isOpen);
    if (highestClosedFromParent) {
        // point to a group element instead
        fromParents = getParents(highestClosedFromParent, state.groupElements);
    }
    const fromParentOffset = getParentOffset(fromParents);

    let nextParents = getParents(nextMapElement, state.groupElements);
    // find the highest nextParent that is closed
    const highestClosedNextParent = nextParents.reverse().find(parent => !parent.isOpen);
    if (highestClosedNextParent) {
        // point to a group element instead
        nextParents = getParents(highestClosedNextParent, state.groupElements);
    }
    // set offset to map element or group element, fall back to no offset when dragging
    const nextParentOffset = (nextMapElement || highestClosedNextParent) ?
        getParentOffset(nextParents) :
        getOffset(0, 0, OFFSET_SIDE.NONE);

    return {
        nextMapElement,
        // Open up parent offsets so Redux knows when they are the same as last time
        // (If it's created as an object Redux will think it's different every time)
        fromParentOffsetX: fromParentOffset.x,
        fromParentOffsetY: fromParentOffset.y,
        nextParentOffsetX: nextParentOffset.x,
        nextParentOffsetY: nextParentOffset.y,
        highestClosedFromParent,
        highestClosedNextParent,
    };
};

export default connect(
    mapStateToProps,
)(Outcome);
